import { track } from "utils/analytics"

// Reports touchpoint_in_view when a CTA marked with
// data-track-exposure is at least half visible in a visible tab: once per
// page and touchpoint (the value of the nearest [data-touchpoint]).
const MARKED_CTAS = "[data-track-exposure]"
const VISIBLE_RATIO = 0.5

const reportedTouchpoints = new Set<string>()

function touchpointOf(cta: Element): string | undefined {
  return cta.closest<HTMLElement>("[data-touchpoint]")?.dataset.touchpoint
}

function report(cta: Element): void {
  const touchpoint = touchpointOf(cta)
  if (!touchpoint || reportedTouchpoints.has(touchpoint)) return

  reportedTouchpoints.add(touchpoint)
  track("touchpoint_in_view", { touchpoint })
}

export default function observeTouchpointExposure(root: ParentNode = document): void {
  if (typeof IntersectionObserver === "undefined") return

  const ctas = Array.from(root.querySelectorAll<HTMLElement>(MARKED_CTAS)).filter(
    (cta) => !cta.dataset.exposureObserved,
  )
  if (!ctas.length) return

  // CTAs visible right now; they count once the tab is visible, too.
  const visibleCtas = new Set<Element>()
  const reportVisible = () => {
    if (document.visibilityState === "visible") visibleCtas.forEach(report)
  }

  // eslint-disable-next-line compat/compat -- guarded by the typeof check above
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
          visibleCtas.add(entry.target)
        } else {
          visibleCtas.delete(entry.target)
        }
      })
      reportVisible()
    },
    { threshold: VISIBLE_RATIO },
  )
  document.addEventListener("visibilitychange", reportVisible)

  ctas.forEach((cta) => {
    cta.setAttribute("data-exposure-observed", "true")
    observer.observe(cta)
  })
}
